import { c as _c } from "react/compiler-runtime";
import { formatGrams } from '@/lib/format/format-weight';
import { useTranslation } from '@/hooks/use-translation';
import { DEMO_DESIGN, DEMO_KEY_COUNT, demoDesigned, demoMeasured } from './demo-sample';
import { barsPath, dotsPath, plotX, plotY, type PlotBox } from './plot';
const BOX: PlotBox = {
  from: 0,
  height: 100,
  max: 20,
  min: 0,
  to: DEMO_KEY_COUNT - 1,
  width: 300,
  x: 10,
  y: 40
};

/** The first key the design leaves unsprung. */
const FIRST_UNSPRUNG = DEMO_DESIGN.springCount + 1;
const DIVIDER = plotX(BOX, FIRST_UNSPRUNG - 1) + 2;

/**
 * What the wippen springs take off the keys, and where they stop.
 *
 * The taper is the point: full tension in the bass, nothing past the last
 * sprung key, so the lead comes out of the keys that need it most. The
 * measured tensions ride above it — the gap is the work to do.
 */
export const SpringsPreview = () => {
  const $ = _c(26);
  const {
    currentLanguage,
    t
  } = useTranslation();
  let t0;
  if ($[0] !== t) {
    t0 = t("The weight the wippen springs take off the measuring point, falling away over the first 48 keys and absent beyond them, against the tensions measured on the piano.");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d={`M0 ${plotY(BOX, 0)}H320`} strokeWidth="1" fill="none" className="stroke-input" />;
    t2 = <path d={barsPath(demoDesigned.springBalance, BOX, 2.6, 0)} opacity="0.55" className="fill-ptl-brass" />;
    t3 = <path d={dotsPath(demoMeasured.springBalance, BOX, 1.5)} opacity="0.8" className="fill-ptl-series-balance" />;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <path d={`M${DIVIDER} ${plotY(BOX, 0)}V${BOX.y}`} strokeWidth="1" strokeDasharray="2 3" fill="none" className="stroke-input" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== currentLanguage || $[7] !== t) {
    t5 = t("{{tension}} \u2192 0 over {{count}} keys", {
      count: DEMO_DESIGN.springCount,
      tension: formatGrams(DEMO_DESIGN.springBaseTension, currentLanguage)
    });
    $[6] = currentLanguage;
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <text x="12" y="20" className="fill-ptl-brass font-mono text-[9.5px]">{t5}</text>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("keys {{first}}\u2013{{last}}", {
      first: FIRST_UNSPRUNG,
      last: DEMO_KEY_COUNT
    });
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <tspan x={DIVIDER + 6}>{t7}</tspan>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t) {
    t9 = t("without springs");
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <tspan x={DIVIDER + 6} dy="13">{t9}</tspan>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t8) {
    t11 = <text x={DIVIDER + 6} y={BOX.y + 10} className="fill-ptl-ink-3 font-mono text-[9.5px]">{t8}{t10}</text>;
    $[19] = t10;
    $[20] = t8;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t0 || $[23] !== t11 || $[24] !== t6) {
    t12 = <svg viewBox="0 0 320 150" className="block min-h-0 w-full flex-1" role="img" aria-label={t0}>{t1}{t2}{t3}{t4}{t6}{t11}</svg>;
    $[22] = t0;
    $[23] = t11;
    $[24] = t6;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
};